import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Users, FileText, Globe, Lock } from 'lucide-react';
import { useSpaces, useCreateSpace } from '@/hooks/queries/useSpaces';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

export function SpacesPage() {
  const { data, isLoading, error } = useSpaces();
  const createSpace = useCreateSpace();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newSpaceName, setNewSpaceName] = useState('');
  const [newSpaceKey, setNewSpaceKey] = useState('');
  const [newSpaceDescription, setNewSpaceDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);

  const handleCreateSpace = async () => {
    if (!newSpaceName.trim() || !newSpaceKey.trim()) return;
    
    try {
      await createSpace.mutateAsync({
        name: newSpaceName,
        key: newSpaceKey.toUpperCase(),
        description: newSpaceDescription || undefined,
        isPublic,
      });
      setShowCreateDialog(false);
      setNewSpaceName('');
      setNewSpaceKey('');
      setNewSpaceDescription('');
      setIsPublic(false);
    } catch (err) {
      console.error('Failed to create space:', err);
    }
  };

  const openCreateDialog = () => {
    setShowCreateDialog(true);
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-4 text-destructive">
        <p>Kunne ikke laste arbeidsområder</p>
      </div>
    );
  }

  const spaces = data?.data || [];

  return (
    <div>
      {/* Create Space Dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-lg">
            <h2 className="text-xl font-bold mb-4">Opprett arbeidsområde</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Navn *</label>
                <input
                  type="text"
                  value={newSpaceName}
                  onChange={(e) => setNewSpaceName(e.target.value)}
                  placeholder="Mitt arbeidsområde"
                  className="mt-1 w-full rounded-md border px-3 py-2"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Nøkkel * (f.eks. ARKITEKTUR)</label>
                <input
                  type="text"
                  value={newSpaceKey}
                  onChange={(e) => setNewSpaceKey(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                  placeholder="MITT"
                  maxLength={10}
                  className="mt-1 w-full rounded-md border px-3 py-2 uppercase"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Beskrivelse</label>
                <textarea
                  value={newSpaceDescription}
                  onChange={(e) => setNewSpaceDescription(e.target.value)}
                  placeholder="Beskrivelse av arbeidsområdet..."
                  rows={3}
                  className="mt-1 w-full rounded-md border px-3 py-2"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isPublic"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  className="h-4 w-4"
                />
                <label htmlFor="isPublic" className="text-sm">Offentlig (alle kan se)</label>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Avbryt
              </Button>
              <Button 
                onClick={handleCreateSpace} 
                disabled={!newSpaceName.trim() || !newSpaceKey.trim() || createSpace.isPending}
              >
                {createSpace.isPending ? 'Oppretter...' : 'Opprett'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Arbeidsområder</h1>
          <p className="text-muted-foreground mt-1">
            Velg et arbeidsområde for å se arkitekturobjekter
          </p>
        </div>
        <Button onClick={openCreateDialog}>
          <Plus className="mr-2 h-4 w-4" />
          Nytt arbeidsområde
        </Button>
      </div>

      {/* Spaces grid */}
      {spaces.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <FileText className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-4 text-lg font-medium">Ingen arbeidsområder</h3>
            <p className="mt-2 text-muted-foreground">
              Opprett ditt første arbeidsområde for å komme i gang
            </p>
            <Button className="mt-4" onClick={openCreateDialog}>
              <Plus className="mr-2 h-4 w-4" />
              Opprett arbeidsområde
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {spaces.map((space) => (
            <Link key={space.id} to={`/spaces/${space.key}`}>
              <Card className="h-full transition-shadow hover:shadow-md">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{space.iconEmoji || '📁'}</span>
                      <div>
                        <CardTitle className="text-lg">{space.name}</CardTitle>
                        <Badge variant={space.isPublic ? 'secondary' : 'outline'} className="mt-1">
                          {space.isPublic ? (
                            <>
                              <Globe className="mr-1 h-3 w-3" />
                              Offentlig
                            </>
                          ) : (
                            <>
                              <Lock className="mr-1 h-3 w-3" />
                              Privat
                            </>
                          )}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="line-clamp-2">
                    {space.description || 'Ingen beskrivelse'}
                  </CardDescription>
                  <div className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {space.memberCount} medlemmer
                    </span>
                    <span className="flex items-center gap-1">
                      <FileText className="h-4 w-4" />
                      {space.objectCount} objekter
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
